import React from 'react';
import { RowError, SimpleProductExt } from '../eve-quick-order';
import { IAdvancedNoteEntity, IManufacturerCodeResponseEntity } from '../../../actions/DataServiceEntities.g';
import { SupersessionKey } from '../../../themes/gardner-theme/models/SupersessionKey';
import { Tooltip } from 'reactstrap';
import { checkAlternateWarehouseFeatureAccess, convertStringToArray } from '../../../utils/alternateWarehouseHelper';
import InventoryAvailabilityModal, { InventoryAvailabilityConfig } from '../../../utils/InventoryAvailabilityModal';
import InventoryClickIcon from '../../../utils/InventoryClickIcon';

export interface IEveQuickOrderRowResources {
    defaultManufacturerCodeText: string;
    deleteText: string;
    productEligibilityText: string;
    supersededProductText: string;
}

interface IEveQuickOrderRowProps {
    id: number;
    onDelete: (id: number) => void;
    onRowChange: (id: number, value: string, quantity: string, manufacturerCode: string) => void;
    onAddRows: () => void;
    value: string;
    quantity: string;
    product: SimpleProductExt | null;
    availableProducts: SimpleProductExt[] | null;
    currencyFormatter: (price: string | number, currencyCode?: string | undefined) => string;
    isLastRow: boolean;
    rowError: RowError;
    onSelectedProductChange: (id: number, selectedProductId: number) => void;
    resources: IEveQuickOrderRowResources;
    manufacturerCodes: IManufacturerCodeResponseEntity[];
    manufacturerCode: string;
    advancedNotes: IAdvancedNoteEntity[];
    // rowEditing: boolean;
    // updateIsEditing: (value: boolean, id: number) => void;
    yourPrice: number | null;
    customerAccNum: string | undefined;
    isSobo: boolean;
    config: InventoryAvailabilityConfig;
    context: any;
    freePriceMask: string;
}

export const EveQuickOrderRow: React.FC<IEveQuickOrderRowProps> = props => {
    const {
        id,
        onDelete,
        value,
        quantity,
        onRowChange,
        product,
        currencyFormatter,
        isLastRow,
        onAddRows,
        manufacturerCodes,
        resources: { defaultManufacturerCodeText, productEligibilityText, supersededProductText },
        rowError,
        manufacturerCode,
        advancedNotes,
        // updateIsEditing,
        yourPrice,
        customerAccNum,
        config,
        isSobo,
        context,
        freePriceMask
    } = props;

    /**
     * Emits the row delete function
     */
    const handleRowDelete = () => onDelete(id);

    // /**
    //  * Emits row change when changing value
    //  * @param e
    //  */
    // const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     onRowChange(id, e.target.value, quantity, manufacturerCode);
    // };

    /**
     * Emits row change when changing quantity
     * @param e
     */
    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onRowChange(id, value, e.target.value, manufacturerCode);
    };

    /**
     * Emits add row function
     */
    const addRows = () => {
        onAddRows();
    };

    const [isToolTipOpen, setIsToolTipOpen] = React.useState<boolean>(false);
    const [isInventoryTooltipOpen, setIsInventoryTooltipOpen] = React.useState<boolean>(false);
    // const [availabilityModalProduct, setAvailabilityModalProduct] = React.useState<number>(0);

    // Determines whether the alternate warehouse feature should be displayed for the user.
    const [shouldDisplayAlternateWarehouseFeature, setShouldDisplayAlternateWarehouseFeature] = React.useState(false);

    const authorizedWarehouseAccounts: string[] = config.authorizedWarehouseAccounts || [];

    // Configuration flag to enable or disable the alternate warehouse feature.
    const isAlternateWarehouseFeatureEnabled: boolean = config.enableAlternateWarehouseFeature;

    // List of dealer types that are authorized to use the alternate warehouse feature.
    const allowedDealerTypes: string = config.allowedDealerTypes || '';

    // List of comma- or semicolon-separated string (e.g., '01,16,99') customer groups that are authorized to use the alternate warehouse feature.
    const designatedCustomerGroups: string = config.designatedCustomerGroups || '';

    // convert customer groups into an array of strings
    const designatedCustomerGroupsArr = convertStringToArray(designatedCustomerGroups);
    const allowedDealerTypesArr = convertStringToArray(allowedDealerTypes);

    const isSupersession = product?.ExtensionProperties?.some(ep => ep.Key === SupersessionKey.SupersededName) ?? false;

    React.useEffect(() => {
        const checkAlternateWarehouseFeature = async () => {
            const shouldDisplayAlternateWarehouse = await checkAlternateWarehouseFeatureAccess({
                extensionProperties: product?.ExtensionProperties,
                allowedDealerTypes: allowedDealerTypesArr,
                designatedCustomerGroupsArr,
                authorizedWarehouseAccounts,
                customerAccNum,
                isAlternateWarehouseFeatureEnabled,
                isSobo: isSobo,
                context
            });
            setShouldDisplayAlternateWarehouseFeature(shouldDisplayAlternateWarehouse);
        };

        checkAlternateWarehouseFeature();
    }, [product]);

    /**
     * Toggle which updates the tooltip state
     * @returns void
     */
    const toggleTooltip = () => setIsToolTipOpen(!isToolTipOpen);

    const toggleInventoryTooltip = () => setIsInventoryTooltipOpen(!isInventoryTooltipOpen);

    const handleManufacturerCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onRowChange(id, value, quantity, e.target.value);
    };

    const [inputValue, setInputValue] = React.useState<string>(value);
    const [userEdited, setUserEdited] = React.useState(false);

    // 2. Keep local state in sync if parent ever changes `value`
    // but don’t override if user already typed something
    React.useEffect(() => {
        if (isSupersession && !userEdited) {
            // force ProductNumber when supersession and untouched
            const fullNumber = product?.ProductNumber || '';
            const productNumberWithoutPrefix = fullNumber.startsWith(manufacturerCode)
                ? fullNumber.slice(manufacturerCode.length)
                : fullNumber;
            setInputValue(productNumberWithoutPrefix);
        } else if (!userEdited) {
            setInputValue(value);
        }
    }, [value, product?.ProductNumber, isSupersession, userEdited]);

    // Reset edit flag whenever parent sends us a new value
    React.useEffect(() => {
        setUserEdited(false);
    }, [value, product?.ProductNumber]);

    // 3. onBlur handler: submit whatever’s in local state
    const handleBlur = () => {
        if (isSupersession && !userEdited) {
            return;
        }
        onRowChange(id, inputValue, quantity, manufacturerCode);
    };

    // 4. onChange only updates local state
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUserEdited(true);
        setInputValue(e.target.value);
    };

    /**
     * Ticket 8282:
     * - The bug caused the tooltip to appear for all products, even when no notes were present, resulting in an empty tooltip on hover.
     * - Fixed by ensuring the "Read Notes" text and tooltip are only shown for products that have actual notes assigned.
     */

    /**
     * Gets the row message to display based on row state
     */
    /**
     * Gets the row message to display based on row state
     */
    const getRowStatusMessage = (product: SimpleProductExt | null, isSupersession: boolean): React.ReactElement | null => {
        if (product) {
            const matchingNotes = advancedNotes.filter(note => note.ItemId === product.ItemId); // Filter notes by ItemId
            const supersessionName =
                product.ExtensionProperties?.find(ep => ep.Key === SupersessionKey.SupersededId)?.Value?.StringValue || '';

            return (
                <>
                    <p>{product.Name}</p>
                    {isSupersession && <p style={{ color: 'red' }}>{supersededProductText.replace('{0}', supersessionName)}</p>}
                    {matchingNotes.length > 0 ? mapNotes(matchingNotes) : null} {/* Display tooltip if there are matching notes */}
                </>
            );
        }

        switch (rowError) {
            case RowError.InvalidQuantity:
                return <p>Invalid Quantity</p>;
            case RowError.ProductNotFound:
                return <p>Product Not Found</p>;
            case RowError.MultipleChoicesAvailable:
                return <p>Please select a manufacturer</p>;
            case RowError.NotEligbile:
                return <p>{productEligibilityText}</p>;
            case RowError.Unavailable:
                return <p>Not Available</p>;
        }

        return null;
    };

    /**
     * Turn array of IAdvancedNoteEntity into JSX element with Tooltip
     *
     * @param {IAdvancedNoteEntity[]} notes the array of notes to display in the tooltip
     * @return {*}
     */
    function mapNotes(notes: IAdvancedNoteEntity[]) {
        // Filter out notes that have an empty or whitespace-only TextValue
        const validNotes = notes.filter(note => note.TextValue && note.TextValue.trim() !== '');

        if (validNotes.length > 0) {
            return (
                <>
                    <a
                        href='javascript:void(0)'
                        data-toggle='tooltip'
                        className='readMore-toolTip'
                        id={`tooltip-${removeSpacesFromItemId(validNotes[0].ItemId!)}`}
                    >
                        Read Notes
                    </a>
                    <Tooltip
                        className='qo-tooltip'
                        placement='right'
                        isOpen={isToolTipOpen}
                        target={`tooltip-${removeSpacesFromItemId(validNotes[0].ItemId!)}`}
                        toggle={toggleTooltip}
                    >
                        <ul className={'advancedNotes'}>
                            {validNotes.map(note => (
                                <li key={note.RecId}>{note.TextValue}</li>
                            ))}
                        </ul>
                    </Tooltip>
                </>
            );
        }

        return null; // Return null if there are no valid notes
    }

    const renderAvailabilityModal = () => {
        if (!product) {
            return;
        }
        return (
            <InventoryAvailabilityModal
                modalProduct={product}
                toggle={() => setIsInventoryTooltipOpen(false)}
                isOpen={isInventoryTooltipOpen}
                config={config}
            />
        );
    };

    // const _renderInventoryClickIcon = (id: number, isRowError: boolean) => {
    //     console.log(id, shouldDisplayAlternateWarehouseFeature, isRowError);

    //     if (!shouldDisplayAlternateWarehouseFeature || isRowError) {
    //         return null;
    //     }

    //     return (
    //         <div>
    //             <span
    //                 id={`Tooltip-${id}`}
    //                 onClick={() => {
    //                     // setTooltipOpen(false);
    //                     setAvailabilityModalProduct(id);
    //                 }}
    //                 data-toggle='tooltip'
    //             >
    //                 <svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 30 30'>
    //                     <path d='M15,3C8.373,3,3,8.373,3,15c0,6.627,5.373,12,12,12s12-5.373,12-12C27,8.373,21.627,3,15,3z M16,21h-2v-7h2V21z M15,11.5 c-0.828,0-1.5-0.672-1.5-1.5s0.672-1.5,1.5-1.5s1.5,0.672,1.5,1.5S15.828,11.5,15,11.5z'></path>
    //                 </svg>
    //             </span>
    //             <Tooltip
    //                 isOpen={isInventoryTooltipOpen}
    //                 target={`Tooltip-${id}`}
    //                 toggle={toggleInventoryTooltip}
    //                 placement='right'
    //                 className='custom-tooltip'
    //             >
    //                 Check availability
    //             </Tooltip>
    //         </div>
    //     );
    // };

    const removeSpacesFromItemId = (itemId: string): string => itemId.replace(/\s/g, '');

    const getAvailability = () => product?.ExtensionProperties?.find(ep => ep.Key === SupersessionKey.Inventory)?.Value?.StringValue || '0';

    return (
        <>
            <div className='q-col q-col-1'>
                <select
                    name='manufacturer-codes-select'
                    id='manufacturer-codes-select'
                    className={`manufacturer-codes-select__dropdown`}
                    value={manufacturerCode}
                    onChange={handleManufacturerCodeChange}
                >
                    {!manufacturerCode && <option value={''}>{defaultManufacturerCodeText}</option>}
                    {manufacturerCodes &&
                        manufacturerCodes.length > 0 &&
                        manufacturerCodes.map(code => (
                            <option value={code.GDNManufacturerCode} key={code.GDNManufacturerCode}>
                                {code.GDNManufacturerCode} - {code.Description}
                            </option>
                        ))}
                </select>
            </div>
            <div className='q-col q-col-2'>
                <div className={'qo-input-group'}>
                    <span className='qo-input'>
                        <input
                            type='text'
                            className='qo-input__input'
                            id={`ipt-${id}`}
                            aria-label='Product'
                            value={inputValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onFocus={() => {
                                if (isLastRow && addRows) {
                                    addRows();
                                }
                            }}
                        />
                    </span>
                </div>
            </div>
            <div className='q-col q-col-3'>
                <div className={'qo-input-group'}>
                    <span className='qo-input'>
                        <input
                            type='text'
                            className='qo-input__input'
                            id={'ipt'}
                            aria-label={'Quantity'}
                            value={quantity}
                            autoComplete='off'
                            onChange={handleQuantityChange}
                            onFocus={() => {
                                if (isLastRow && addRows) {
                                    addRows();
                                }
                                // handleFocus();
                            }}
                            // onBlur={handleBlur}
                        />
                    </span>
                </div>
            </div>
            <div className='q-col q-col-4'>{getRowStatusMessage(product, isSupersession)}</div>
            <div className='q-col q-col-5'>
                {product && <p>{product.AdjustedPrice !== 0 ? currencyFormatter(product.AdjustedPrice) : freePriceMask}</p>}
            </div>
            <div className='q-col q-col-6'>
                {product && product.AdjustedPrice === 0 ? (
                    <p>{freePriceMask}</p>
                ) : product && !yourPrice ? (
                    <div className='q-col q-col-6__loading-div'>
                        <p className='q-col q-col-6__loading-div__loading' />
                    </div>
                ) : (
                    product && <p>{currencyFormatter(yourPrice!)}</p>
                )}
            </div>

            <div className='q-col q-col-7'>
                {product && (
                    <div className={shouldDisplayAlternateWarehouseFeature && rowError === RowError.None ? 'inventory-availability' : ''}>
                        {getAvailability()}{' '}
                        <InventoryClickIcon
                            id={id}
                            onClick={toggleInventoryTooltip}
                            shouldDisplay={shouldDisplayAlternateWarehouseFeature && rowError === RowError.None}
                        />
                    </div>
                )}
                {renderAvailabilityModal()}
            </div>
            <div className='q-col q-col-8'>
                <button className={'qo-button'} onClick={handleRowDelete}>
                    <span className='q-col-8__delete-icon'>
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            fill='currentColor'
                            className='bi bi-trash'
                            viewBox='0 0 16 16'
                        >
                            <path d='M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z' />
                            <path
                                fillRule='evenodd'
                                d='M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z'
                            />
                        </svg>
                    </span>
                    {/* <span className='q-col-6__delete-text'>{deleteText}</span> */}
                </button>
            </div>
        </>
    );
};
